import { useCallback, useMemo, useState } from "react";
import {
  MassText,
  MassTextAttachment,
  NotificationType,
} from "@markit/common.types";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { API } from "../../../../API";
import {
  calculateTextMessageCreditsEstimate,
  detectedCensored,
  massTextMessageConverter,
} from "@markit/common.utils";
import { Colors } from "../../../../utils/colors";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { BackButton } from "../../../Buttons/BackButton";
import RectangleButton from "../../../Buttons/RectangleButton";
import { MassTextMessageDrafting } from "../../MassTexts/Message/MassTextMessageDrafting";
import ConfirmDiscardChangesModal from "../../../Containers/ConfirmPopups/ConfirmDiscardChangesModal";
import { useMassTextManagement } from "../../../../hooks/useMassTextManagement";
import ContentViolationAlert from "../../../DeliveryScore/ContentViolationAlert";
import { checkValidTextMessage } from "../../../../utils/textingUtils";
import AlertContainer from "../../../Containers/AlertContainer";
import { showNotificationBanner } from "../../../../utils/notificationUtils";

type DMAutomationMassTextPanelProps = {
  initialMassText: MassText;
  initialMassTextAttachments: MassTextAttachment[];
  isEditing?: boolean;
  backOnPress: () => void;
  onSaveContent: (massText: MassText) => void;
};

const DMAutomationMassTextPanel = (props: DMAutomationMassTextPanelProps) => {
  const {
    initialMassText,
    initialMassTextAttachments,
    isEditing,
    backOnPress,
    onSaveContent,
  } = props;
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountState).account;
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showContentViolationAlert, setShowContentViolationAlert] =
    useState(false);
  const [alertText, setAlertText] = useState<{
    heading: string;
    subHeading: string;
  }>({ heading: "", subHeading: "" });
  const [saving, setSaving] = useState(false);

  const { massTextSettings, updateMassTextSettings } = useMassTextManagement({
    massText: initialMassText,
    eventId: "",
    campaign: undefined,
    isDuplicateText: false,
  });

  const madeChanges = useMemo(
    () =>
      massTextSettings.message !== initialMassText.message ||
      massTextSettings.mediaUrl !== initialMassText.mediaUrl,
    [
      massTextSettings.message,
      massTextSettings.mediaUrl,
      initialMassText.message,
      initialMassText.mediaUrl,
    ]
  );

  const convertedMessage = useMemo(
    () =>
      massTextMessageConverter(
        massTextSettings.message,
        initialMassTextAttachments,
        accountData.trustedSender
      ),
    [
      massTextSettings.message,
      initialMassTextAttachments,
      accountData.trustedSender,
    ]
  );

  const numCredits = useMemo(
    () =>
      calculateTextMessageCreditsEstimate(
        convertedMessage,
        accountData.trustedSender,
        massTextSettings.mediaUrl,
        undefined
      ),
    [convertedMessage, accountData.trustedSender, massTextSettings.mediaUrl]
  );

  const onSaveMessage = useCallback(async () => {
    const { valid, alertHeading, alertSubHeading } = checkValidTextMessage(
      massTextSettings.message,
      numCredits
    );
    if (!valid) {
      setAlertText({ heading: alertHeading, subHeading: alertSubHeading });
      return;
    }
    if (detectedCensored(massTextSettings.message)) {
      setShowContentViolationAlert(true);
      return;
    }
    if (isEditing) {
      setSaving(true);
      await API.text.updateMassTextMessage({
        userId: accountData.uid,
        massText: massTextSettings,
      });
      showNotificationBanner(
        dispatch,
        "Text Message Updated!",
        NotificationType.AFFIRMATIVE
      );
      setSaving(false);
    }
    onSaveContent(massTextSettings);
  }, [
    massTextSettings,
    numCredits,
    isEditing,
    onSaveContent,
    accountData.uid,
    dispatch,
  ]);

  return (
    <>
      <div
        className="ColumnNormal PreventScroll"
        style={{ backgroundColor: Colors.WHITE }}
      >
        <div
          className="AlignedRowSpaced"
          style={{ flexShrink: 0, height: 58, paddingInline: 14 }}
        >
          <div className="AlignedRow" style={{ gap: 10 }}>
            <BackButton
              onPress={() => {
                if (madeChanges) {
                  setShowDiscardModal(true);
                } else {
                  backOnPress();
                }
              }}
              iconName="ion:chevron-back"
            />
            <span className="sectionTitle">Send a Text Message</span>
          </div>
          <RectangleButton
            buttonLabel="Save"
            onPress={onSaveMessage}
            loading={saving}
            disabled={!madeChanges}
            altColor={Colors.BLACK}
            altTextColor={Colors.WHITE}
            altBorderRadius={100}
            containerStyles={{ padding: "10px 14px", width: "fit-content" }}
          />
        </div>
        <HorizontalDivider altMargin={0} />
        <div className="ColumnNormal">
          <div className="AlignedRow" style={{ padding: 14, gap: 10 }}>
            <span className="bodySubtext">To</span>
            <span className="bodyMedium">Started Flow</span>
          </div>
          <HorizontalDivider altMargin={0} />
          <div className="AlignedRow" style={{ padding: 14, gap: 10 }}>
            <span className="bodySubtext">When</span>
            <span className="bodyMedium">Sent Immediately</span>
          </div>
          <HorizontalDivider altMargin={0} />
        </div>
        <MassTextMessageDrafting
          campaign={undefined}
          massText={massTextSettings}
          updateMassTextSettings={updateMassTextSettings}
          numCredits={numCredits}
          numFollowersNotCompliant={1}
          showComplianceCreditNotice={false}
          convertedMessage={convertedMessage}
          finalMessageWithAppendages={convertedMessage}
          disableEventLinks={false}
          massTextAttachments={initialMassTextAttachments}
        />
      </div>
      <ConfirmDiscardChangesModal
        isVisible={showDiscardModal}
        setIsVisible={setShowDiscardModal}
        deleteOnPress={backOnPress}
      />
      <ContentViolationAlert
        message={massTextSettings.message}
        isVisible={showContentViolationAlert}
        setIsVisible={setShowContentViolationAlert}
      />
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </>
  );
};

export default DMAutomationMassTextPanel;
